import type { AvailableField, ConnectionFieldsDto, RestField, SoftwareDto } from '@bk/contracts';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, errorMessage } from '../../lib/api';
import { dateTime, humanize } from '../../lib/format';
import { useTenant, useTenantQuery } from '../../lib/useTenant';
import { Button, ErrorText } from '../../ui';

/** Nome interno da un percorso della risposta: `fields.assignee` → assignee. */
const nameOf = (path: string, taken: Set<string>) => {
  const base =
    (path.split('.').filter((p) => p !== 'fields').at(-1) ?? path)
      .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '')
      .replace(/^(\d)/, 'c_$1')
      .slice(0, 60) || 'campo';
  let name = base;
  for (let i = 2; taken.has(name); i++) name = `${base}_${i}`;
  return name;
};

/** Tipo proposto dall'esempio: date ISO e numeri, altrimenti testo. */
const kindOf = (a: AvailableField): RestField['tipo'] => (/^\d{4}-\d{2}-\d{2}/.test(a.example) ? 'data' : /^-?\d+([.,]\d+)?$/.test(a.example) ? 'numero' : 'testo');

function ResourceFields({ s, resource, onSaved }: { s: SoftwareDto; resource: ConnectionFieldsDto['resources'][number]; onSaved: (d: ConnectionFieldsDto) => void }) {
  const { t } = useTenant();
  const [fields, setFields] = useState(resource.fields);
  const [adding, setAdding] = useState('');
  const dirty = JSON.stringify(fields) !== JSON.stringify(resource.fields);
  const used = new Set(fields.map((f) => f.path));
  const free = resource.available.filter((a) => !used.has(a.path));
  const save = useMutation({
    mutationFn: () => api<ConnectionFieldsDto>(t(`/software/${s.id}/connection/fields`), { body: { resource: resource.key, fields } }),
    onSuccess: onSaved,
  });
  return (
    <div className="rounded-lg border border-black/10 p-3">
      <p className="mb-2 text-xs font-medium text-ink/70">
        {resource.label} · {fields.length} campi letti{resource.available.length ? ` su ${resource.available.length} trovati nei dati` : ''}
      </p>
      <ul className="flex flex-wrap gap-1.5">
        {fields.map((f) => (
          <li key={f.name} className="flex items-center gap-1 rounded-full bg-black/5 py-0.5 pl-2.5 pr-1 text-xs" title={`${f.path} · ${f.tipo}`}>
            {f.label ?? humanize(f.name)}
            <button
              className="rounded-full px-1 text-ink/40 hover:bg-black/10 hover:text-ink disabled:opacity-30"
              disabled={fields.length <= 1}
              aria-label={`Togli ${f.label ?? f.name}`}
              onClick={() => setFields(fields.filter((x) => x.name !== f.name))}
            >
              ×
            </button>
          </li>
        ))}
      </ul>
      {free.length > 0 && fields.length < 60 && (
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <select className="min-w-0 max-w-full flex-1 rounded-lg border border-black/15 bg-white px-2 py-1.5 text-xs" value={adding} onChange={(e) => setAdding(e.target.value)}>
            <option value="">Aggiungi un campo…</option>
            {free.map((a) => (
              <option key={a.path} value={a.path}>
                {a.path} — es. {a.example} ({a.present}/{a.sample})
              </option>
            ))}
          </select>
          <Button
            variant="ghost"
            disabled={!adding}
            onClick={() => {
              const a = free.find((x) => x.path === adding);
              if (!a) return;
              setFields([...fields, { name: nameOf(a.path, new Set(fields.map((f) => f.name))), path: a.path, tipo: kindOf(a) }]);
              setAdding('');
            }}
          >
            Aggiungi
          </Button>
        </div>
      )}
      {dirty && (
        <div className="mt-2 flex items-center gap-2">
          <Button disabled={save.isPending} onClick={() => save.mutate()}>
            {save.isPending ? 'Provo e salvo…' : 'Salva e rileggi tutto'}
          </Button>
          <Button variant="ghost" onClick={() => setFields(resource.fields)}>
            Annulla
          </Button>
        </div>
      )}
      <ErrorText>{save.error ? errorMessage(save.error) : ''}</ErrorText>
    </div>
  );
}

/** Campi letti da un programma collegato tramite API (ADR 0012): scelti dal sistema per l'azienda, modificabili. */
export function ConnectionFields({ s }: { s: SoftwareDto }) {
  const { tenant, t } = useTenant();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const busy = !!s.connection?.choosing_fields;
  const q = useTenantQuery<ConnectionFieldsDto>(['software', s.id, 'fields', busy], `/software/${s.id}/connection/fields`, { enabled: open });
  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['tenant', tenant.id, 'software'] });
  const choose = useMutation({
    mutationFn: () => api<SoftwareDto>(t(`/software/${s.id}/connection/fields/choose`), { method: 'POST' }),
    onSuccess: refresh,
  });
  const choice = s.connection?.fields_choice;
  return (
    <div className="mt-2">
      <p className="text-xs text-ink/55">
        {busy
          ? 'Il sistema sta scegliendo i campi utili all’azienda da un campione dei dati…'
          : choice
            ? `${choice.note} (${dateTime(choice.at)})`
            : 'Si leggono i campi di base: il sistema sceglie quelli utili all’azienda alla prossima lettura.'}{' '}
        <button className="text-accent hover:underline" onClick={() => setOpen(!open)}>
          {open ? 'Chiudi i campi' : 'Vedi i campi'}
        </button>
      </p>
      {open && (
        <div className="mt-2 space-y-2">
          <ErrorText>{q.error ? errorMessage(q.error) : choose.error ? errorMessage(choose.error) : ''}</ErrorText>
          {q.data?.resources.map((r) => (
            <ResourceFields
              key={`${r.key}:${JSON.stringify(r.fields)}`}
              s={s}
              resource={r}
              onSaved={(d) => {
                queryClient.setQueryData(['tenant', tenant.id, 'software', s.id, 'fields', busy], d);
                refresh();
              }}
            />
          ))}
          <Button variant="ghost" disabled={busy || choose.isPending || !!s.connection?.syncing} onClick={() => choose.mutate()}>
            {busy ? 'Scelta in corso…' : 'Fai scegliere di nuovo i campi al sistema'}
          </Button>
        </div>
      )}
    </div>
  );
}
